import React, { Fragment, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ReactComponent as BrandDetailsIcon } from 'assets/icons/brand-details.svg';
import { ReactComponent as MetadataIcon } from 'assets/icons/metadata.svg';
import { ReactComponent as CreateBrandIcon } from 'assets/icons/create-brand.svg';
import { ReactComponent as PreviewIcon } from 'assets/icons/preview.svg';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import BottomNavigation from 'components/bottom-navigation';
import { SuccessModal } from 'components/base/success-modal';
import useCokitchenContext from 'contexts/cokitchen.context';
import RightSidebar from 'components/right-sidebar';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { Breadcrumbs, ToastMessage } from '@cokitchen/cokitchen-components';
import { CoKitchenType } from 'utils/types';
import useDashboardContext from 'contexts/dashboard.context';
import { MapIcon } from 'cokitchen-icons';
import { ENUM_COKITCHEN_STATUS } from 'utils/enum';

const CreateCokitchenContent = () => {
  const { pathname } = useLocation();
  const { setRightSection } = useDashboardContext();

  const [showSuccess, setShowSuccess] = useState(false);
  const { disabled } = useCokitchenContext();

  const { createCokitchenForm, newCokitchen, setNewCokitchen } = useCokitchenContext();

  const CreateCokitchenNav = [
    {
      title: 'Create CoKitchen',
      link: `${ROUTE_KEYS.CREATE_COKITCHEN}/kitchen`,
      Icon: BrandDetailsIcon
    },
    {
      title: 'Brands setting',
      link: `${ROUTE_KEYS.CREATE_COKITCHEN}/brands`,
      Icon: CreateBrandIcon
    },
    {
      title: 'Preview',
      link: `${ROUTE_KEYS.CREATE_COKITCHEN}/preview`,
      Icon: PreviewIcon
    }
  ];

  const polygonExploreNav = [
    {
      title: 'Polygon settings',
      link: `${ROUTE_KEYS.CREATE_COKITCHEN}/polygon`,
      Icon: MapIcon
    },
    {
      title: 'Explore settings',
      link: `${ROUTE_KEYS.CREATE_COKITCHEN}/explore`,
      Icon: MetadataIcon
    }
  ];

  const isPolygonExplore = [
    `${ROUTE_KEYS.CREATE_COKITCHEN}/${ROUTE_KEYS.CREATE_COKITCHEN_POLYGON}`,
    `${ROUTE_KEYS.CREATE_COKITCHEN}/${ROUTE_KEYS.CREATE_COKITCHEN_EXPLORE}`
  ].includes(pathname);

  useEffect(() => {
    if (isPolygonExplore)
      setRightSection(<RightSidebar list={polygonExploreNav} title='Cokitchen details' />);
    else setRightSection(<RightSidebar list={CreateCokitchenNav} title='Cokitchen details' />);

    return () => {
      setRightSection(null);
    };
  }, [createCokitchenForm.cokitchen_status, pathname, newCokitchen]);

  const [loading, setLoading] = useState(false);

  const getPayloadFields = (createCokitchenForm: CoKitchenType) => {
    if (createCokitchenForm.cokitchen_status === ENUM_COKITCHEN_STATUS.POPUP) {
      const { event_image, brand_ids, feed_ids, active, cover_images, cover_videos } =
        createCokitchenForm;
      return {
        event_image,
        brand_ids,
        feed_ids,
        active,
        cover_images,
        cover_videos
      };
    }
    return {
      brand_ids: createCokitchenForm.brand_ids,
      ...(createCokitchenForm.event_image && { event_image: createCokitchenForm.event_image }),
      cover_images: createCokitchenForm.cover_images,
      cover_videos: createCokitchenForm.cover_videos
    };
  };

  const {
    name,
    address,
    customer_service_phone_number,
    customer_service_whatsapp,
    frontdesk_phone_number,
    lat,
    lng,
    cokitchen_status
  } = createCokitchenForm;
  const payload = {
    name,
    address,
    lat,
    lng,
    customer_service_phone_number: customer_service_phone_number.length
      ? customer_service_phone_number
      : [],
    customer_service_whatsapp: customer_service_whatsapp || [],
    frontdesk_phone_number: frontdesk_phone_number || [],
    ...getPayloadFields(createCokitchenForm)
  };

  const createCokitchen = async () => {
    setLoading(true);
    const url =
      cokitchen_status === ENUM_COKITCHEN_STATUS.POPUP
        ? 'marketing/pop/cokitchen'
        : 'marketing/cokitchen';

    try {
      const res = await apiService.post(url, {
        data: payload,
        params: {
          ...((createCokitchenForm.cokitchen_status === ENUM_COKITCHEN_STATUS.DINE_IN ||
            createCokitchenForm.cokitchen_status === ENUM_COKITCHEN_STATUS.STANDARD) && {
            type: createCokitchenForm.cokitchen_status
          })
        }
      });
      setNewCokitchen(res.data.data);
      setShowSuccess(true);
      setLoading(false);
    } catch (err) {
      toast(<ToastMessage type='error' message='Error creating Cokitchen' />);
    } finally {
      setLoading(false);
    }
  };

  const breadCrumbs = [
    {
      name: 'Overview',
      path: `${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.OVERVIEW}`
    },
    {
      name: 'Create cokitchen',
      path: ''
    }
  ];

  return (
    <Fragment>
      <Breadcrumbs breadcrumbs={breadCrumbs} />
      <Outlet />
      {!isPolygonExplore && (
        <BottomNavigation
          list={CreateCokitchenNav}
          onConfirm={createCokitchen}
          disabled={disabled}
          loading={loading}
        />
      )}
      <SuccessModal
        onClose={() => setShowSuccess(false)}
        show={showSuccess}
        link={`${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.CREATE_COKITCHEN}/polygon`}
        linkText='Proceed to add polygon & explore posts'>
        {cokitchen_status === ENUM_COKITCHEN_STATUS.POPUP
          ? 'Pop up kitchen'
          : cokitchen_status === ENUM_COKITCHEN_STATUS.DINE_IN
          ? 'Dine in kitchen'
          : 'Cokitchen'}
        &nbsp;created successfully
      </SuccessModal>
    </Fragment>
  );
};

export default CreateCokitchenContent;
